import { Mail } from 'lucide-react';

type NewsletterEmailEnvelopeProps = {
  emailSubject: string;
  preheader: string;
  newsletterName: string;
  onEmailSubjectChange: (value: string) => void;
  onPreheaderChange: (value: string) => void;
};

const fieldLabel =
  'block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5';
const fieldInput =
  'w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-emerald-500';

export default function NewsletterEmailEnvelope({
  emailSubject,
  preheader,
  newsletterName,
  onEmailSubjectChange,
  onPreheaderChange,
}: NewsletterEmailEnvelopeProps) {
  return (
    <section className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/30 overflow-hidden">
      <div className="px-4 sm:px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
        <Mail className="w-4 h-4 text-gray-400 shrink-0" />
        <div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Inbox</h3>
          <p className="text-[11px] text-gray-500 dark:text-gray-400">
            What subscribers see in their mail app — separate from the internal newsletter name.
          </p>
        </div>
      </div>
      <div className="px-4 sm:px-5 py-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div>
          <label className={fieldLabel} htmlFor="newsletter-email-subject">
            Email subject
          </label>
          <input
            id="newsletter-email-subject"
            type="text"
            value={emailSubject}
            onChange={(e) => onEmailSubjectChange(e.target.value)}
            className={fieldInput}
            placeholder="What subscribers see in their inbox…"
          />
          <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
            {emailSubject.trim()
              ? 'Used when the send is materialized.'
              : newsletterName.trim()
                ? `Empty — will use “${newsletterName.trim()}” as fallback.`
                : 'Empty — will use the newsletter name as fallback.'}
          </p>
        </div>
        <div>
          <label className={fieldLabel} htmlFor="newsletter-preheader">
            Preheader
          </label>
          <textarea
            id="newsletter-preheader"
            value={preheader}
            onChange={(e) => onPreheaderChange(e.target.value)}
            className={`${fieldInput} min-h-[42px] resize-y`}
            rows={2}
            placeholder="Optional preview line after the subject…"
          />
          <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
            Inbox preview line; Postmark merge field{' '}
            <span className="font-mono">{'{{subtitle}}'}</span>.
          </p>
        </div>
      </div>
    </section>
  );
}
